import { useState, useEffect } from 'react'

/**
 * Custom hook to manage fullscreen state
 * @returns {[boolean, Function]} A tuple containing the fullscreen state and a toggle function
 */
export default function useFullscreenState() {
  const [isFullScreen, setIsFullScreen] = useState(false)

  // Function to toggle fullscreen state
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Enter full screen
      document.body.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`)
      })
    } else {
      // Exit full screen
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit full-screen mode: ${err.message}`)
      })
    }
    // Use the functional form of setState when the new state depends on the previous state
    setIsFullScreen((prevState) => !prevState)
  }

  // Listen for fullscreen changes
  useEffect(() => {
    // Define the event handler inside the effect to avoid stale closures
    const handleFullScreenChange = () => {
      // Use the functional form of setState when the new state depends on the previous state
      setIsFullScreen(!!document.fullscreenElement)
    }

    // Add the event listener
    document.addEventListener('fullscreenchange', handleFullScreenChange)

    // Clean up the event listener on unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
    }
    // Empty dependency array is correct here since we don't use any values from the component scope
    // that would need to be refreshed when they change
  }, [])

  return [isFullScreen, toggleFullscreen]
}
